import { useEffect } from "react";

import { useAppDispatch, useAppSelector } from "../Store/Hooks";
import {
  fetchXFactorAccountPipelineStatus,
  fetchXFactorBriefingsForAccount,
} from "../Store/Reducers/XFactorBriefingSlice";
import type {
  XFactorBriefingSummaryRow,
  XFactorDigestData,
  XFactorPipelineStatus,
} from "../Types/XFactorBriefing";

export type { XFactorPipelineStatus };

export type XFactorAccountBriefingState = {
  /** Newest live briefing for this EB account, if any. */
  latest: XFactorBriefingSummaryRow | null;
  data: XFactorDigestData | null | undefined;
  loading: boolean;
  error: string | null;
  /** Bulk approve pipeline status while briefing is still generating. */
  pipeline: XFactorPipelineStatus | null;
};

/**
 * Resolves an EB account to its newest live xfactor briefing and payload.
 *
 * While a bulk approve pipeline is still generating, polls
 * `POST /account-pipeline-status` so the UI can show "Briefing generating…"
 * instead of a false empty message.
 */
export const useXFactorAccountBriefing = (
  accountId?: string
): XFactorAccountBriefingState => {
  const dispatch = useAppDispatch();
  const list = useAppSelector(s => s.xfactorBriefing.list);
  const pipelineRow = useAppSelector(s =>
    accountId ? (s.xfactorBriefing.pipelineStatus[accountId] ?? null) : null
  );

  useEffect(() => {
    if (accountId) dispatch(fetchXFactorBriefingsForAccount(accountId));
  }, [dispatch, accountId]);

  // Newest first from the API; the first row is the current briefing.
  const latest =
    accountId && list.customerId === accountId && list.items.length
      ? list.items[0]
      : null;

  useEffect(() => {
    if (!accountId || latest?.data) return;

    let cancelled = false;
    const load = async () => {
      const result = await dispatch(
        fetchXFactorAccountPipelineStatus([accountId])
      );
      if (
        cancelled ||
        !fetchXFactorAccountPipelineStatus.fulfilled.match(result)
      ) {
        return;
      }
      // Refresh live briefing when a generate just finished.
      if (result.payload[accountId]?.briefing_status === "succeeded") {
        dispatch(fetchXFactorBriefingsForAccount(accountId));
      }
    };

    void load();
    const id = window.setInterval(load, 20000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [accountId, latest?.data, dispatch]);

  return {
    latest,
    data: latest?.data,
    loading: list.loading && !latest,
    error: list.error,
    pipeline: accountId && !latest?.data ? pipelineRow : null,
  };
};

export default useXFactorAccountBriefing;
